/**
 * Service worker registration helper. Lazy-importable from main.tsx so the
 * registration logic stays out of the critical render path.
 *
 * Phase 4.6 of the migration plan. The SW itself lives at /public/sw.js
 * and gets copied to the root of the build output by Vite.
 *
 * Skip rules:
 *  - SSR / Node — no `navigator` global
 *  - Dev mode — HMR uses fetch interception; SW would compete
 *  - Tests (Vitest jsdom) — `navigator.serviceWorker` may be missing
 *  - Hard opt-out via VITE_SERVICE_WORKER="false"
 */

export type RegisterServiceWorkerOptions = {
  /** Override the path. Defaults to "/sw.js". */
  path?: string;
  /** Skip registration even in production. Used by the smoke test. */
  enabled?: boolean;
};

export function registerServiceWorker(options: RegisterServiceWorkerOptions = {}): void {
  const { path = "/sw.js", enabled } = options;
  if (typeof window === "undefined") return;
  // Use truthy access rather than `in navigator` because secure-context-only
  // builds expose the property but return undefined.
  if (!navigator.serviceWorker) return;

  if (enabled === false) return;

  // import.meta.env.DEV is true in vite dev server.
  if (enabled !== true && import.meta.env.DEV) return;

  const flag = import.meta.env.VITE_SERVICE_WORKER as string | undefined;
  if (flag && flag.toLowerCase() === "false") return;

  const registerNow = () => {
    navigator.serviceWorker.register(path).catch((err: unknown) => {
      console.warn("[tree-hole sw] registration failed", err);
    });
  };

  if (document.readyState === "complete") {
    registerNow();
  } else {
    window.addEventListener("load", registerNow, { once: true });
  }
}
